import type { ProductPrice } from "@shared/types";
import { PRODUCT_UOMS } from "@shared/types";
import { Button } from "@shared-ui/button";
import { Input } from "@shared-ui/input";
import { Label } from "@shared-ui/label";
import { Textarea } from "@shared-ui/textarea";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type JSX, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { priceLabel, uomLabel } from "@/features/catalog/lib/format";
import {
	type AdminVariant,
	getPriceHistory,
	type PriceBasis,
	type PriceHistoryEntry,
	type PriceNotShownReason,
	type PriceObservationStatus,
	type PriceVerification,
	type RecordPriceInput,
	type RecordPriceResult,
	recordPrice,
	verifyPriceObservation,
} from "@/features/catalog/services/catalog-api.service";
import { apiErrorMessage } from "@/lib/api-error";
import { formatCalendarDate, formatPaise, rupeesToPaise } from "@/lib/format";

export { rupeesToPaise };

/**
 * Bases a customer can be shown, then bases that are stored and never shown.
 *
 * TUPLES, not an object keyed by basis: the price-source ratchet
 * (`apps/backend/scripts/price-source-check.py`) reads an object key named
 * after the legacy column as a write to it. Labels follow `BASIS_LABEL` in the
 * backend's pricing service, so the form and the history table name a basis
 * the same way.
 */
const SHOWN_BASES: ReadonlyArray<readonly [PriceBasis, string]> = [
	["dealer_net", "Trade price"],
	["mrp_incl_gst", "MRP incl. GST"],
	["mrp", "MRP"],
	["list_price_ex_gst", "List price + GST"],
	["ex_works_trade", "Ex-works + GST"],
	["recommended_consumer", "Recommended price"],
	["street_indicative", "Indicative street price"],
	["avg_realisation", "Average realised price"],
];

const STORED_ONLY_BASES: ReadonlyArray<readonly [PriceBasis, string]> = [
	["mrp_basis_unstated", "MRP, GST treatment unstated"],
	["mrp_and_dealer_net", "MRP and dealer net (composite)"],
	["ex_factory", "Ex-factory"],
	["distributor_net", "Distributor net"],
	["project_institutional", "Project/institutional price"],
	["landed", "Landed cost"],
];

const SHOWN_BASIS_LABEL = new Map<string, string>(SHOWN_BASES);

/** Where a suggested basis came from, so the note under the select can say. */
export type BasisSuggestion =
	| { basis: PriceBasis; from: "shown" | "recorded" }
	| { basis: ""; from: null };

/**
 * The basis a new price for one item starts on.
 *
 * In order: the basis customers see for that item; the basis of the latest
 * open, published price recorded for that item; and, for an item with
 * neither, the same two for the product's headline price and any item. Only a
 * basis that can be shown is suggested -- starting on a stored-only basis
 * would make the new price as invisible as the last.
 *
 * `variantId` is the "Applies to" choice: "" for the whole product.
 */
export function defaultBasis(
	variantId: string,
	price: ProductPrice | null | undefined,
	prices: readonly ProductPrice[] | undefined,
	history: readonly PriceHistoryEntry[] | undefined,
): BasisSuggestion {
	const projectionId = variantId || PRODUCT_LEVEL_VARIANT_ID;
	const historyId = variantId || null;
	const shownFor = (prices ?? []).find(
		(row) => row.variantId === projectionId && SHOWN_BASIS_LABEL.has(row.basis),
	);
	if (shownFor) return { basis: shownFor.basis as PriceBasis, from: "shown" };

	const open = (history ?? [])
		.filter(
			(entry) =>
				entry.status === "published" &&
				entry.effectiveTo === null &&
				SHOWN_BASIS_LABEL.has(entry.basis),
		)
		.sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom));
	const recordedFor = open.find((entry) => entry.variantId === historyId);
	if (recordedFor) return { basis: recordedFor.basis, from: "recorded" };

	if (price && SHOWN_BASIS_LABEL.has(price.basis)) {
		return { basis: price.basis as PriceBasis, from: "shown" };
	}
	if (open[0]) return { basis: open[0].basis, from: "recorded" };
	return { basis: "", from: null };
}

/** The price history, one query shared by the form and the history table. */
function usePriceHistory(productId: string) {
	return useQuery({
		queryKey: priceHistoryKey(productId),
		queryFn: () => getPriceHistory(productId),
	});
}

const STATUS_OPTIONS: ReadonlyArray<readonly [PriceObservationStatus, string]> =
	[
		["published", "Published"],
		["on_request", "On request"],
		["not_published", "Not published"],
	];

const STATUS_LABEL = new Map(STATUS_OPTIONS);

const VERIFICATION_LABEL: Record<PriceVerification, string> = {
	unverified: "Unverified",
	auto_validated: "Auto-validated",
	human_reviewed: "Checked by a person",
	brand_confirmed: "Confirmed by the brand",
};

const ALREADY_CHECKED: ReadonlySet<PriceVerification> = new Set([
	"human_reviewed",
	"brand_confirmed",
]);

/**
 * The projection's id for a price that applies to the whole product; the
 * history sends `null` instead. Mirrors `PRODUCT_LEVEL_VARIANT_ID` in the
 * backend's pricing service.
 */
const PRODUCT_LEVEL_VARIANT_ID = "00000000-0000-0000-0000-000000000000";

/** Query key for a product's price history. */
export function priceHistoryKey(productId: string): readonly string[] {
	return ["admin-catalog-price-history", productId];
}

/** Today in the operator's own calendar, as a date input wants it. */
function today(): string {
	const now = new Date();
	const month = String(now.getMonth() + 1).padStart(2, "0");
	const day = String(now.getDate()).padStart(2, "0");
	return `${now.getFullYear()}-${month}-${day}`;
}

function variantLabel(variant: AdminVariant): string {
	const values = [variant.variantAxis1Value, variant.variantAxis2Value].filter(
		Boolean,
	);
	return values.length > 0
		? `${variant.sku} (${values.join(", ")})`
		: variant.sku;
}

function isWebUrl(value: string): boolean {
	return /^https?:\/\//i.test(value);
}

interface ProductPricingProps {
	productId: string;
	/** The product's saved unit, which a new price defaults to. */
	uom: string;
	variants: AdminVariant[];
	price: ProductPrice | null | undefined;
	prices: ProductPrice[] | undefined;
	/**
	 * A `draft` is hidden from customers, prices and all. An `inactive` product
	 * is withdrawn from sale but its page still resolves, with its price, so
	 * quotes that reference it keep rendering.
	 */
	productStatus: string;
	canWrite: boolean;
	canVerify: boolean;
}

/**
 * The Pricing tab: the price customers see, a form to record one, and every
 * observation behind it.
 *
 * Price is not a product column. Each price is a dated, sourced observation, and
 * what customers see is whichever one the projection picks. So this tab saves
 * on its own and never touches the editor's draft.
 */
export function ProductPricing({
	productId,
	uom,
	variants,
	price,
	prices,
	productStatus,
	canWrite,
	canVerify,
}: ProductPricingProps): JSX.Element {
	const variantById = new Map(variants.map((variant) => [variant.id, variant]));
	const appliesTo = (variantId: string | null): string => {
		if (!variantId || variantId === PRODUCT_LEVEL_VARIANT_ID) {
			return "Whole product";
		}
		const variant = variantById.get(variantId);
		// The product read lists live variants only, so an id it lacks belongs to
		// one that has been retired.
		return variant ? variantLabel(variant) : "Retired variant";
	};

	return (
		<div className="grid gap-6">
			{productStatus !== "draft" ? null : (
				<p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
					This product is not published, so customers see none of its prices
					until it is. "Shown to customers" below means shown once it is
					published.
				</p>
			)}
			<CurrentPrice price={price} prices={prices} appliesTo={appliesTo} />
			{canWrite ? (
				<RecordPriceForm
					productId={productId}
					uom={uom}
					price={price}
					prices={prices}
					variants={variants}
					productStatus={productStatus}
					canVerify={canVerify}
				/>
			) : null}
			<PriceHistory
				productId={productId}
				canVerify={canVerify}
				appliesTo={appliesTo}
			/>
		</div>
	);
}

function CurrentPrice({
	price,
	prices,
	appliesTo,
}: {
	price: ProductPrice | null | undefined;
	prices: ProductPrice[] | undefined;
	appliesTo: (variantId: string | null) => string;
}): JSX.Element {
	const rows = prices && prices.length > 0 ? prices : price ? [price] : [];

	return (
		<section className="grid gap-2">
			<h3 className="text-sm font-semibold">Current price</h3>
			{rows.length === 0 ? (
				<p className="text-sm text-muted-foreground">
					No price is shown to customers yet.
				</p>
			) : (
				<>
					<p className="text-2xl font-semibold tabular-nums">
						{priceLabel({ price, prices })}
					</p>
					<ul className="grid gap-1 text-sm">
						{rows.map((row) => (
							<li
								key={`${row.variantId}:${row.city}`}
								className="flex flex-wrap items-center gap-2"
							>
								<span className="font-medium">{appliesTo(row.variantId)}</span>
								<span className="tabular-nums">
									{formatPaise(row.amount)} / {uomLabel(row.uom)}
								</span>
								<span className="text-muted-foreground">{row.basisLabel}</span>
								<span className="text-muted-foreground">
									observed {formatCalendarDate(row.observedAt)}
								</span>
								{row.isStale ? (
									<Badge variant="warning">Past refresh date</Badge>
								) : null}
							</li>
						))}
					</ul>
				</>
			)}
		</section>
	);
}

interface PriceDraft {
	variantId: string;
	basis: PriceBasis | "";
	status: PriceObservationStatus;
	amount: string;
	uom: string;
	effectiveFrom: string;
	sourceUrl: string;
	evidence: string;
	checked: boolean;
}

function emptyDraft(uom: string): PriceDraft {
	return {
		variantId: "",
		basis: "",
		status: "published",
		amount: "",
		uom,
		effectiveFrom: today(),
		sourceUrl: "",
		evidence: "",
		checked: false,
	};
}

function RecordPriceForm({
	productId,
	uom,
	price,
	prices,
	variants,
	productStatus,
	canVerify,
}: {
	productId: string;
	uom: string;
	price: ProductPrice | null | undefined;
	prices: ProductPrice[] | undefined;
	variants: AdminVariant[];
	productStatus: string;
	canVerify: boolean;
}): JSX.Element {
	const queryClient = useQueryClient();
	const [draft, setDraft] = useState<PriceDraft>(() => emptyDraft(uom));
	const [result, setResult] = useState<RecordedResult | null>(null);
	/*
	 * Read here only for the basis default, which is DERIVED each render rather
	 * than copied into the draft: a history that arrives after mount moves the
	 * default without overwriting a basis the operator chose.
	 */
	const history = usePriceHistory(productId);
	const suggestion = defaultBasis(draft.variantId, price, prices, history.data);
	const basis = draft.basis || suggestion.basis;

	const set = <K extends keyof PriceDraft>(key: K, value: PriceDraft[K]) =>
		setDraft((current) => ({ ...current, [key]: value }));

	const published = draft.status === "published";
	const amountPaise = rupeesToPaise(draft.amount);
	const amountRejected =
		published && draft.amount.trim() !== "" && amountPaise === undefined;

	const missing: string[] = [];
	if (basis === "") missing.push("a basis");
	if (published && amountPaise === undefined) missing.push("an amount");
	if (draft.effectiveFrom === "") missing.push("an effective date");

	const record = useMutation({
		mutationFn: (input: RecordPriceInput) => recordPrice(productId, input),
		onSuccess: async (saved, input) => {
			setResult({ ...saved, enteredPaise: input.amountPaise ?? null });
			setDraft(emptyDraft(uom));
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: ["admin-catalog-product", productId],
				}),
				queryClient.invalidateQueries({ queryKey: priceHistoryKey(productId) }),
				queryClient.invalidateQueries({ queryKey: ["admin-catalog-products"] }),
			]);
		},
	});

	const submit = (): void => {
		if (basis === "" || missing.length > 0) return;
		setResult(null);
		record.mutate({
			basis,
			status: draft.status,
			...(published && amountPaise !== undefined ? { amountPaise } : {}),
			uom: draft.uom,
			variantId: draft.variantId || null,
			/*
			 * Today goes as the current moment, not as a date. A date is read as
			 * midnight UTC, which is still in the future before 05:30 in India --
			 * refused by the server, and a second price recorded the same day
			 * would collide with the first on the same instant.
			 */
			effectiveFrom:
				draft.effectiveFrom === today()
					? new Date().toISOString()
					: draft.effectiveFrom,
			sourceUrl: draft.sourceUrl.trim() || null,
			basisEvidence: draft.evidence.trim() || null,
			...(draft.checked && canVerify
				? { verification: "human_reviewed" as const }
				: {}),
		});
	};

	const pending = record.isPending;

	return (
		<section className="grid gap-4 rounded-2xl border p-4">
			<div>
				<h3 className="text-sm font-semibold">Record a price</h3>
				<p className="text-xs text-muted-foreground">
					Saved on its own, separately from the editor&apos;s Save button. A
					variant&apos;s price is recorded here by choosing that variant.
				</p>
			</div>

			<div className="grid gap-4 md:grid-cols-2">
				<div className="grid gap-1.5">
					<Label htmlFor="price-applies-to">Applies to</Label>
					<select
						id="price-applies-to"
						className="h-9 rounded-md border px-2 text-sm"
						value={draft.variantId}
						disabled={pending}
						onChange={(event) => set("variantId", event.target.value)}
					>
						<option value="">Whole product</option>
						{variants.map((variant) => (
							<option key={variant.id} value={variant.id}>
								{variantLabel(variant)}
							</option>
						))}
					</select>
				</div>

				<div className="grid gap-1.5">
					<Label htmlFor="price-basis">Basis</Label>
					<select
						id="price-basis"
						className="h-9 rounded-md border px-2 text-sm"
						value={basis}
						disabled={pending}
						onChange={(event) =>
							set("basis", event.target.value as PriceBasis | "")
						}
					>
						{basis === "" ? (
							<option value="">Choose what this number is</option>
						) : null}
						<optgroup label="Can be shown to customers">
							{SHOWN_BASES.map(([value, label]) => (
								<option key={value} value={value}>
									{label}
								</option>
							))}
						</optgroup>
						<optgroup label="Stored, never shown to customers">
							{STORED_ONLY_BASES.map(([value, label]) => (
								<option key={value} value={value}>
									{label}
								</option>
							))}
						</optgroup>
					</select>
					<p className="text-xs text-muted-foreground">
						{draft.basis !== "" || suggestion.from === null
							? "The same figure is a different price as MRP and as dealer net."
							: suggestion.from === "shown"
								? "Starts on the basis customers see now. Check it: the same figure is a different price as MRP and as dealer net."
								: "Starts on the basis of the latest price recorded. Check it: the same figure is a different price as MRP and as dealer net."}
					</p>
				</div>

				<div className="grid gap-1.5">
					<Label htmlFor="price-status">Status</Label>
					<select
						id="price-status"
						className="h-9 rounded-md border px-2 text-sm"
						value={draft.status}
						disabled={pending}
						onChange={(event) =>
							set("status", event.target.value as PriceObservationStatus)
						}
					>
						{STATUS_OPTIONS.map(([value, label]) => (
							<option key={value} value={value}>
								{label}
							</option>
						))}
					</select>
				</div>

				<div className="grid gap-1.5">
					<Label htmlFor="price-amount">Amount (₹)</Label>
					<Input
						id="price-amount"
						inputMode="decimal"
						placeholder="480.50"
						value={published ? draft.amount : ""}
						disabled={pending || !published}
						onChange={(event) => set("amount", event.target.value)}
					/>
					{published ? null : (
						<p className="text-xs text-muted-foreground">
							A price on request or not published has no amount.
						</p>
					)}
					{amountRejected ? (
						<p role="alert" className="text-xs text-red-700">
							Enter rupees, with at most two decimal places.
						</p>
					) : null}
				</div>

				<div className="grid gap-1.5">
					<Label htmlFor="price-uom">Unit</Label>
					<select
						id="price-uom"
						className="h-9 rounded-md border px-2 text-sm"
						value={draft.uom}
						disabled={pending}
						onChange={(event) => set("uom", event.target.value)}
					>
						{PRODUCT_UOMS.map((one) => (
							<option key={one} value={one}>
								{uomLabel(one)}
							</option>
						))}
					</select>
				</div>

				<div className="grid gap-1.5">
					<Label htmlFor="price-effective-from">Effective from</Label>
					<Input
						id="price-effective-from"
						type="date"
						max={today()}
						value={draft.effectiveFrom}
						disabled={pending}
						onChange={(event) => set("effectiveFrom", event.target.value)}
					/>
				</div>

				<div className="grid gap-1.5 md:col-span-2">
					<Label htmlFor="price-source-url">Source URL</Label>
					<Input
						id="price-source-url"
						type="url"
						placeholder="https://example.com/price-list.pdf"
						value={draft.sourceUrl}
						disabled={pending}
						onChange={(event) => set("sourceUrl", event.target.value)}
					/>
				</div>

				<div className="grid gap-1.5 md:col-span-2">
					<Label htmlFor="price-evidence">Evidence</Label>
					<Textarea
						id="price-evidence"
						maxLength={2000}
						placeholder="The line from the price list, e.g. 'MRP inclusive of all taxes'"
						value={draft.evidence}
						disabled={pending}
						onChange={(event) => set("evidence", event.target.value)}
					/>
				</div>

				<div className="grid gap-1 md:col-span-2">
					<label className="flex items-center gap-2 text-sm">
						<input
							type="checkbox"
							checked={draft.checked && canVerify}
							disabled={pending || !canVerify}
							onChange={(event) => set("checked", event.target.checked)}
						/>
						I checked this price against its source
					</label>
					{canVerify ? null : (
						<p className="text-xs text-muted-foreground">
							Marking a price as checked needs the verify permission, so this
							price will be recorded as unverified.
						</p>
					)}
				</div>
			</div>

			{record.isError ? (
				<p role="alert" className="text-sm text-red-700">
					{apiErrorMessage(record.error, "Could not record this price.")}
				</p>
			) : null}

			{result ? (
				<RecordedMessage result={result} productStatus={productStatus} />
			) : null}

			<div className="flex flex-wrap items-center gap-3">
				<Button onClick={submit} disabled={pending || missing.length > 0}>
					{pending ? "Recording…" : "Record price"}
				</Button>
				{missing.length > 0 ? (
					<span className="text-xs text-muted-foreground">
						Needs {missing.join(", ")}.
					</span>
				) : null}
			</div>
		</section>
	);
}

/** The saved result, with the amount the operator typed, to compare against. */
interface RecordedResult extends RecordPriceResult {
	enteredPaise: number | null;
}

const NOT_SHOWN_TEXT: Record<PriceNotShownReason, string> = {
	no_amount:
		"It records that no amount is published, so there is no number to show.",
	later_price_recorded:
		"A price dated after it, and already in effect, is recorded on this basis, so this one was saved as history.",
	basis_not_shown:
		"Prices on this basis are stored for reference and never shown to customers.",
	no_hsn_code:
		"Neither this product nor its category has an HSN code, so no GST rate can be found. Set the HSN code on the Basics tab, then record this price again dated today.",
	no_gst_rate:
		"No GST rate is on record for this product's HSN code on the date this price takes effect, so its price including GST cannot be worked out.",
	preferred_price_has_no_gst_rate:
		"Another recorded price for the same item ranks higher and is the one used, but no GST rate is on record for its date, so no price is shown for the item this price applies to.",
	another_price_preferred:
		"Another recorded price for the same item takes precedence -- one on a higher-ranked basis, or on the same basis and checked against its source -- and customers see that one. The history below marks it.",
};

/** The rate as a person writes it: 1800 basis points is "18%". */
function percent(rateBp: number): string {
	return `${rateBp / 100}%`;
}

/**
 * Why the number customers see is not the number typed, when it is not.
 *
 * A basis that excludes GST is shown with GST added, so Rs 1,000 entered as a
 * list price is Rs 1,180 to a customer. Said outright, because without it the
 * difference reads as a save that went wrong (#247).
 */
function conversionSentence(result: RecordedResult): string | null {
	const shown = result.customerPrice;
	if (!result.shown || !shown || result.enteredPaise === null) return null;
	// Inclusive: nothing was added. Equal: a nil-rated item, 0% added.
	if (shown.isGstInclusive || shown.amountPaise === result.enteredPaise) {
		return null;
	}
	return `The price shown is ${formatPaise(shown.amountPaise)}: the ${formatPaise(result.enteredPaise)} entered excludes GST, so ${percent(shown.gstRateBp)} GST is added.`;
}

/** The not-shown reason, naming the HSN code where the reason is about a rate. */
function notShownText(result: RecordedResult): string {
	const reason = result.notShownReason;
	if (!reason) return "";
	if (reason === "no_gst_rate" && result.hsnCode) {
		return `No GST rate for HSN ${result.hsnCode} is on record for the date this price takes effect, so its price including GST cannot be worked out. The price is stored. A price for this item is shown once a rate for HSN ${result.hsnCode} covering the date of the price used for it is added under Catalogue > GST rates, which re-prices it.`;
	}
	if (reason === "preferred_price_has_no_gst_rate" && result.hsnCode) {
		return `Another recorded price for the same item ranks higher and is the one used, but no GST rate for HSN ${result.hsnCode} is on record for its date, so no price is shown for the item this price applies to.`;
	}
	return NOT_SHOWN_TEXT[reason];
}

function RecordedMessage({
	result,
	productStatus,
}: {
	result: RecordedResult;
	productStatus: string;
}): JSX.Element {
	if (!result.shown) {
		const instead = result.customerPrice;
		return (
			<div
				role="status"
				className="grid gap-1 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
			>
				<p className="font-medium">Price saved, but customers do not see it.</p>
				<p>{notShownText(result)}</p>
				{instead ? (
					<p>
						The price used for this item instead is{" "}
						{formatPaise(instead.amountPaise)} (
						{SHOWN_BASIS_LABEL.get(instead.basis) ?? instead.basis}).
					</p>
				) : null}
			</div>
		);
	}
	const conversion = conversionSentence(result);
	return (
		<div
			role="status"
			className="grid gap-1 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm"
		>
			<p className="font-medium">Price recorded.</p>
			<p>
				{productStatus === "draft"
					? "This is now the product's current price. Customers see it once the product is published."
					: productStatus === "inactive"
						? "This is now the product's current price. The product is withdrawn from sale, so customers see it only where the product already appears, such as existing quotes."
						: "Customers now see this price."}
			</p>
			{conversion ? <p>{conversion}</p> : null}
		</div>
	);
}

function PriceHistory({
	productId,
	canVerify,
	appliesTo,
}: {
	productId: string;
	canVerify: boolean;
	appliesTo: (variantId: string | null) => string;
}): JSX.Element {
	const queryClient = useQueryClient();
	const history = usePriceHistory(productId);

	const verify = useMutation({
		mutationFn: (observationId: string) =>
			verifyPriceObservation(observationId, "human_reviewed"),
		onSuccess: async () => {
			// The product read too: verification ranks candidates, so marking one
			// checked can change which price customers are shown.
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: priceHistoryKey(productId) }),
				queryClient.invalidateQueries({
					queryKey: ["admin-catalog-product", productId],
				}),
				queryClient.invalidateQueries({ queryKey: ["admin-catalog-products"] }),
			]);
		},
	});

	return (
		<section className="grid gap-2">
			<h3 className="text-sm font-semibold">Price history</h3>
			{history.isPending ? (
				<p className="text-sm text-muted-foreground">Loading price history…</p>
			) : history.isError ? (
				<p role="alert" className="text-sm text-red-700">
					{apiErrorMessage(history.error, "Could not load the price history.")}
				</p>
			) : history.data.length === 0 ? (
				<p className="text-sm text-muted-foreground">
					No price has been recorded for this product.
				</p>
			) : (
				<>
					{verify.isError ? (
						<p role="alert" className="text-sm text-red-700">
							{apiErrorMessage(verify.error, "Could not mark this price.")}
						</p>
					) : null}
					<div className="overflow-x-auto">
						<table className="w-full text-left text-sm">
							<thead className="text-xs text-muted-foreground">
								<tr>
									<th className="py-2 pr-3 font-medium">Effective</th>
									<th className="py-2 pr-3 font-medium">Applies to</th>
									<th className="py-2 pr-3 font-medium">Basis</th>
									<th className="py-2 pr-3 font-medium">Amount</th>
									<th className="py-2 pr-3 font-medium">Source</th>
									<th className="py-2 pr-3 font-medium">Checked</th>
								</tr>
							</thead>
							<tbody>
								{history.data.map((entry) => (
									<HistoryRow
										key={entry.id}
										entry={entry}
										appliesTo={appliesTo}
										canVerify={canVerify}
										verifying={verify.isPending}
										onVerify={() => verify.mutate(entry.id)}
									/>
								))}
							</tbody>
						</table>
					</div>
				</>
			)}
		</section>
	);
}

function HistoryRow({
	entry,
	appliesTo,
	canVerify,
	verifying,
	onVerify,
}: {
	entry: PriceHistoryEntry;
	appliesTo: (variantId: string | null) => string;
	canVerify: boolean;
	verifying: boolean;
	onVerify: () => void;
}): JSX.Element {
	return (
		<tr className="border-t align-top">
			<td className="py-2 pr-3 whitespace-nowrap">
				{formatCalendarDate(entry.effectiveFrom)}
				{" – "}
				{entry.effectiveTo ? formatCalendarDate(entry.effectiveTo) : "no end"}
				{entry.isCited ? (
					<div className="mt-1">
						<Badge variant="success">Shown to customers</Badge>
					</div>
				) : null}
			</td>
			<td className="py-2 pr-3">{appliesTo(entry.variantId)}</td>
			<td className="py-2 pr-3">
				{entry.basisLabel}
				{entry.notProjectableReason ? (
					<p className="text-xs text-muted-foreground">
						{entry.notProjectableReason}
					</p>
				) : null}
			</td>
			<td className="py-2 pr-3 tabular-nums whitespace-nowrap">
				{entry.status === "published"
					? `${formatPaise(entry.amount)} / ${uomLabel(entry.uom)}`
					: STATUS_LABEL.get(entry.status)}
			</td>
			<td className="py-2 pr-3">
				{entry.source}
				{entry.sourceUrl && isWebUrl(entry.sourceUrl) ? (
					<>
						{" "}
						<a
							href={entry.sourceUrl}
							target="_blank"
							rel="noopener noreferrer"
							className="text-xs underline"
						>
							Open source
						</a>
					</>
				) : null}
				{entry.basisEvidence ? (
					<p className="text-xs text-muted-foreground">{entry.basisEvidence}</p>
				) : null}
			</td>
			<td className="py-2 pr-3">
				{VERIFICATION_LABEL[entry.verification] ?? entry.verification}
				{canVerify && !ALREADY_CHECKED.has(entry.verification) ? (
					<div className="mt-1">
						<Button
							size="sm"
							variant="outline"
							disabled={verifying}
							onClick={onVerify}
						>
							Mark checked
						</Button>
					</div>
				) : null}
			</td>
		</tr>
	);
}
